// SPDX-License-Identifier: GPL-3.0-or-later
// The fixed camera. It never moves while the game runs. It shows the whole playfield in a
// portrait window and in a landscape window. The window size sets the distance only.
//
// The camera looks down at the middle of the playfield, tilted a little toward the player so
// that the table has depth. The distance is the smallest one at which all eight corners of the
// playfield box are inside the view, with a small margin.

import { PerspectiveCamera, Vector3 } from "three";

/** The box that the camera must show, in table units. */
export interface PlayfieldBounds {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
  /** Height of the box above the playfield. */
  readonly height: number;
}

/** Tilt from straight down, in degrees. */
export const CAMERA_TILT_DEG = 20;
/** Vertical field of view, in degrees. */
export const CAMERA_FOV_DEG = 30;
/** Part of the view that stays empty at each edge. */
export const CAMERA_MARGIN = 0.03;

/** Make the camera. Call `fitCamera` after each resize. */
export function createCamera(): PerspectiveCamera {
  return new PerspectiveCamera(CAMERA_FOV_DEG, 1, 10, 100_000);
}

/** The eight corners of the box, in the three.js frame. */
export function boundsCorners(bounds: PlayfieldBounds): Vector3[] {
  const out: Vector3[] = [];
  for (const x of [bounds.left, bounds.right]) {
    for (const z of [bounds.top, bounds.bottom]) {
      for (const y of [0, bounds.height]) out.push(new Vector3(x, y, z));
    }
  }
  return out;
}

/**
 * True when every corner is inside the view, with the margin. The camera matrices must be
 * current. `extra` is a list of more points that must be in the view (the corners of a tall
 * character that stands out of the playfield box).
 */
export function cornersInView(
  camera: PerspectiveCamera,
  bounds: PlayfieldBounds,
  margin = CAMERA_MARGIN,
  extra: readonly Vector3[] = [],
): boolean {
  const limit = 1 - margin;
  const probe = new Vector3();
  for (const corner of [...boundsCorners(bounds), ...extra]) {
    probe.copy(corner).project(camera);
    if (Math.abs(probe.x) > limit || Math.abs(probe.y) > limit || probe.z < -1 || probe.z > 1)
      return false;
  }
  return true;
}

function place(camera: PerspectiveCamera, target: Vector3, distance: number): void {
  const tilt = (CAMERA_TILT_DEG * Math.PI) / 180;
  camera.position.set(
    target.x,
    target.y + Math.cos(tilt) * distance,
    target.z + Math.sin(tilt) * distance,
  );
  camera.near = Math.max(10, distance * 0.2);
  camera.far = distance * 4;
  camera.up.set(0, 1, 0);
  camera.lookAt(target);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);
}

/**
 * Set the aspect ratio and the distance so that the whole box and the extra points are in view.
 * The camera looks at the middle of the box and the points on the playfield.
 * Returns the distance. The result depends only on the aspect, the bounds and the points.
 */
export function fitCamera(
  camera: PerspectiveCamera,
  aspect: number,
  bounds: PlayfieldBounds,
  extra: readonly Vector3[] = [],
): number {
  camera.aspect = Number.isFinite(aspect) && aspect > 0 ? aspect : 1;
  const xs = [bounds.left, bounds.right, ...extra.map((p) => p.x)];
  const zs = [bounds.top, bounds.bottom, ...extra.map((p) => p.z)];
  const target = new Vector3(
    (Math.min(...xs) + Math.max(...xs)) / 2,
    0,
    (Math.min(...zs) + Math.max(...zs)) / 2,
  );
  let near = 50;
  let far = 200_000;
  // Bisection. The box fits at `far` and does not fit at `near`.
  for (let i = 0; i < 48; i++) {
    const mid = (near + far) / 2;
    place(camera, target, mid);
    if (cornersInView(camera, bounds, CAMERA_MARGIN, extra)) far = mid;
    else near = mid;
  }
  place(camera, target, far);
  return far;
}
